import React from "react";
import { Box, Typography, Grid, Paper, Stack, Chip } from "@mui/material";
import { useLogger } from "../../../../contexts/LoggerContext";
import { useSchemes } from "../../../../contexts/SchemesContext";
import {
  FieldGoalPlayLog,
  FieldGoalPlaySelection,
} from "../../../../types/LogTypes";
import { jerseyNumberNameDisplay } from "../../../../helpers/helpers";

export const FieldGoalDetails = () => {
  const { currentPlayCall, currentPlayLog, currentPlayPenalty } = useLogger();
  const { fieldGoalPersonnel } = useSchemes();

  const playCall = currentPlayCall as FieldGoalPlaySelection | null;
  const playLog = currentPlayLog as FieldGoalPlayLog | null;

  const personnel_group = playCall
    ? fieldGoalPersonnel.find((p) => p.id === playCall.personnel_group_id)
    : null;

  const getResultColor = (result: "GOOD" | "MISSED" | "BLOCKED") => {
    switch (result) {
      case "GOOD":
        return "success";
      case "MISSED":
        return "error";
      case "BLOCKED":
        return "warning";
      default:
        return "primary";
    }
  };

  const CalculateGridSize = () => {
    if (currentPlayPenalty) {
      return 4;
    }
    return 6;
  };
  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={3}>
        {/* Play Call Section */}
        <Grid item xs={12} md={CalculateGridSize()}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Call
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Personnel
                </Typography>
                <Typography variant="h6">
                  {personnel_group?.name || ""}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Kick Type
                </Typography>
                <Typography variant="h6">
                  {playCall?.kick_type === "PAT" ? "Extra Point" : "Field Goal"}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Play Result Section */}
        <Grid item xs={12} md={CalculateGridSize()}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
              position: "relative",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Result
            </Typography>

            {playLog && (
              <Stack spacing={2}>
                {/* Kicker */}
                {playLog.kicker_roster_player && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Kicker
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {jerseyNumberNameDisplay(playLog.kicker_roster_player)}
                    </Typography>
                  </Box>
                )}

                {/* Field Goal Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {playLog.pat ? "Extra Point" : "Field Goal"} Details
                  </Typography>
                  <Typography variant="body1">
                    Distance: {playLog.field_goal_distance} yards
                  </Typography>
                  <Chip
                    label={playLog.field_goal_result}
                    color={getResultColor(playLog.field_goal_result)}
                    sx={{ mt: 1 }}
                  />
                </Box>

                {/* Turnover Section */}
                {(playLog.fumble || playLog.interception) && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: "error.main",
                      color: "error.contrastText",
                      borderRadius: 1,
                    }}
                  >
                    {playLog.fumble && (
                      <>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          FUMBLE
                        </Typography>
                        {playLog.fumble.fumble_roster_player && (
                          <Typography variant="body2">
                            Fumbled by:{" "}
                            {jerseyNumberNameDisplay(
                              playLog.fumble.fumble_roster_player
                            )}
                          </Typography>
                        )}
                        {playLog.fumble.fumble_recovery_roster_player && (
                          <Typography variant="body2">
                            Recovered by:{" "}
                            {jerseyNumberNameDisplay(
                              playLog.fumble.fumble_recovery_roster_player
                            )}
                          </Typography>
                        )}
                      </>
                    )}
                    {playLog.interception && (
                      <>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          INTERCEPTION
                        </Typography>
                        {playLog.interception.interception_roster_player && (
                          <Typography variant="body2">
                            Intercepted by:{" "}
                            {jerseyNumberNameDisplay(
                              playLog.interception.interception_roster_player
                            )}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Penalties Section */}
        {currentPlayPenalty && (
          <Grid item xs={12} md={CalculateGridSize()}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                backgroundColor: "warning.light",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: "warning.dark", fontWeight: 600 }}
              >
                Penalties
              </Typography>
              {/* Add penalty details here */}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
